'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@gartner-digital-markets/nimbus-v3-ui';
import { type ReactNode, useRef } from 'react';
import type { ModalFormCloseSource } from '../../core';
import { cn } from '../ui/utils';

interface FormModalProps {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  dataTestId?: string;
  onClose: (source: ModalFormCloseSource) => void;
}

export const FormModal = ({
  open,
  title,
  description,
  children,
  className,
  contentClassName,
  contentStyle,
  dataTestId,
  onClose,
}: FormModalProps) => {
  const closeSource = useRef<ModalFormCloseSource>('close-button');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open) {
          onClose(closeSource.current);
          closeSource.current = 'close-button';
        }
      }}
    >
      <DialogContent
        data-testid={dataTestId}
        className={cn('gap-0 p-0', className)}
        style={contentStyle}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={() => {
          closeSource.current = 'keyboard';
        }}
        onPointerDownOutside={() => {
          closeSource.current = 'close-outside';
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {/* min-w-0 stops a long unwrapped child (e.g. a selected option
            in a SelectTrigger with `whitespace-nowrap`) from forcing this
            grid item — and the DialogContent itself — wider than the
            viewport on mobile. */}
        <div className={cn('min-w-0', contentClassName)}>{children}</div>
      </DialogContent>
    </Dialog>
  );
};
